import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch, ApiError } from '../../lib/api.js';
import { useToast } from '../../components/ui/Toast.js';

interface CredentialOption {
  id: string;
  name: string;
  kind: string;
  isActive: boolean;
}

interface AvailableVersion {
  ref: string;
  kind: 'tag' | 'branch';
  sha: string;
  name: string;
  isNewer: boolean;
}

interface UpdateStatus {
  current: { sha: string; describe: string };
  available: AvailableVersion[];
  inProgress: boolean;
  last: { ref: string; status: string; error?: string; finishedAt?: string } | null;
  githubError: string | null;
}

const DEFAULT_KEYS = [
  { key: 'default_do_token_credential_id', label: 'Default DigitalOcean token', kind: 'do_token' },
  { key: 'default_cloudflare_token_credential_id', label: 'Default Cloudflare token', kind: 'cloudflare_token' },
  { key: 'default_ssh_key_credential_id', label: 'Default SSH deploy key', kind: 'ssh_key' },
] as const;

function UpdatesCard() {
  const toast = useToast();
  const [selected, setSelected] = useState('');
  const [confirming, setConfirming] = useState<AvailableVersion | null>(null);

  const status = useQuery<UpdateStatus>({
    queryKey: ['update-status'],
    queryFn: () => apiFetch('/settings/update/status'),
    refetchInterval: (q) => (q.state.data?.inProgress ? 3000 : false),
    retry: true,
  });

  const trigger = useMutation({
    mutationFn: (v: AvailableVersion) =>
      apiFetch('/settings/update', { method: 'POST', body: { ref: v.ref, sha: v.sha } }),
    onSuccess: () => {
      setConfirming(null);
      toast.success('Update started — the portal will restart shortly');
      void status.refetch();
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'failed to start update'),
  });

  const data = status.data;
  const updating = data?.inProgress ?? false;
  const chosen = data?.available.find((a) => a.ref === selected) ?? null;

  return (
    <div className="card" style={{ padding: 24, maxWidth: 560, display: 'flex', flexDirection: 'column', gap: 14 }}>
      <p className="page-eyebrow">Portal updates</p>
      {!data ? (
        <p className="mono dim">{status.isError ? 'portal restarting…' : 'loading…'}</p>
      ) : (
        <>
          <p className="mono">current: {data.current.describe || data.current.sha.slice(0, 7)}</p>

          {updating ? (
            <p className="mono dim">update in progress… (the portal may briefly go offline)</p>
          ) : (
            <>
              {data.githubError ? (
                <p className="dim">
                  {data.githubError}{' '}
                  <a href="/credentials">Credentials</a> · <a href="/account">Account</a>
                </p>
              ) : null}
              {data.last ? (
                <p className="dim">
                  last update: {data.last.status}
                  {data.last.error ? ` — ${data.last.error}` : ''}
                </p>
              ) : null}
              {data.available.length > 0 ? (
                <div style={{ display: 'flex', gap: 10 }}>
                  <select
                    className="mono"
                    value={selected}
                    onChange={(e) => setSelected(e.target.value)}
                    style={{ flex: 1 }}
                  >
                    <option value="">select a version…</option>
                    {data.available.map((v) => (
                      <option key={v.ref} value={v.ref}>
                        {v.name}
                        {v.isNewer ? ' (new)' : ''} — {v.sha.slice(0, 7)}
                      </option>
                    ))}
                  </select>
                  <button
                    className="btn btn-amber"
                    disabled={!chosen}
                    onClick={() => chosen && setConfirming(chosen)}
                  >
                    Update
                  </button>
                </div>
              ) : null}
            </>
          )}
        </>
      )}

      {confirming ? (
        <div className="dialog-backdrop" onClick={() => setConfirming(null)}>
          <div className="card dialog" onClick={(e) => e.stopPropagation()}>
            <h2>
              Update to <span className="mono">{confirming.name}</span>?
            </h2>
            <p className="dim">
              This rebuilds and restarts the portal. It will be unavailable for ~1–2 minutes. On
              failure it automatically rolls back to the current version.
            </p>
            <div className="dialog-actions">
              <button className="btn" onClick={() => setConfirming(null)}>
                Cancel
              </button>
              <button
                className="btn btn-amber"
                disabled={trigger.isPending}
                onClick={() => trigger.mutate(confirming)}
              >
                {trigger.isPending ? 'Starting…' : 'Update & restart'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function SettingsPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { data: settings } = useQuery<Record<string, unknown>>({
    queryKey: ['settings'],
    queryFn: () => apiFetch('/settings'),
  });
  const { data: credentials } = useQuery<CredentialOption[]>({
    queryKey: ['credentials'],
    queryFn: () => apiFetch('/credentials'),
  });
  const [draft, setDraft] = useState<Record<string, string | boolean | null>>({});

  const save = useMutation({
    mutationFn: () => apiFetch('/settings', { method: 'PUT', body: draft }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['settings'] });
      setDraft({});
      toast.success('Settings saved');
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'failed to save'),
  });

  if (!settings) return <p className="mono dim">loading…</p>;

  const value = (key: string): string =>
    String(draft[key] ?? settings[key] ?? '');
  const require2fa = (draft.require_2fa ?? settings.require_2fa ?? true) === true;

  return (
    <div>
      <header className="page-header">
        <div>
          <p className="page-eyebrow">Administration</p>
          <h1>Settings</h1>
        </div>
      </header>
      <div className="card" style={{ padding: 24, maxWidth: 560, display: 'flex', flexDirection: 'column', gap: 18 }}>
        <label className="check-row" style={{ fontSize: 14, display: 'flex', gap: 10 }}>
          <input
            type="checkbox"
            checked={require2fa}
            onChange={(e) => setDraft({ ...draft, require_2fa: e.target.checked })}
          />
          Require two-factor authentication for all users
        </label>

        {DEFAULT_KEYS.map(({ key, label, kind }) => {
          const options = (credentials ?? []).filter((c) => c.kind === kind && c.isActive);
          return (
            <div className="field" key={key}>
              <label>{label}</label>
              <select
                value={value(key)}
                onChange={(e) => setDraft({ ...draft, [key]: e.target.value || null })}
                style={{
                  background: 'var(--ink)',
                  border: '1px solid var(--ink-line)',
                  borderRadius: 8,
                  color: 'var(--snow)',
                  padding: '10px 12px',
                  fontSize: 14,
                }}
              >
                <option value="">
                  {options.length === 1 ? `auto (${options[0]!.name})` : 'auto (single active credential)'}
                </option>
                {options.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          );
        })}

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            className="btn btn-amber"
            disabled={Object.keys(draft).length === 0 || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Saving…' : 'Save settings'}
          </button>
        </div>
      </div>
      <div style={{ marginTop: 18 }}>
        <UpdatesCard />
      </div>
    </div>
  );
}
